<template>
  <div class="staff-info-container">
    <div class="staff-create">
      <div class="action-pane">
        <div class="w-70 staff-header px-3">
          <h5 class="title my-0">{{ $t("feature.staff.create") }}</h5>
          <span>กรุณากรอกข้อมูลให้ครบถ้วน</span>
        </div>
        <div class="w-30 mx-3">
          <button class="sc-btn sc-btn-opaque" @click="createStaffAccount">
            {{ $t("feature.staff.new") }}
          </button>
        </div>
      </div>
      <section class="staff-meta">
        <div class="form">
          <dl class="form-options">
            <dt>โปรไฟล์</dt>
            <dd>
              <label class="align-right">
                <span>อีเมล</span>
                <el-input v-model="staff.email" type="text"></el-input>
              </label>
            </dd>
            <dd>
              <label class="align-right">
                <span>ชื่อ</span>
                <el-input v-model="staff.firstName" type="text"></el-input>
              </label>
            </dd>
            <dd>
              <label class="align-right">
                <span>สกุล</span>
                <el-input v-model="staff.lastName" type="text"></el-input>
              </label>
            </dd>
            <dd>
              <label class="align-right">
                <span>ใช้ email password</span>
                <el-checkbox
                  v-model="staff.isCorpPass"
                  @input="handleIsCorpPassChange"
                ></el-checkbox>
              </label>
            </dd>
            <dd>
              <label class="align-right">
                <span>รหัสผ่าน</span>
                <el-input
                  v-model="staff.password"
                  :disabled="staff.isCorpPass"
                  type="text"
                  placeholder="ความยาวตั้งแต่ 8 ตัวอักษรขึ้นไป"
                ></el-input>
              </label>
            </dd>
            <dd>
              <label class="align-right">
                <span>Referral Code</span>
                <el-input v-model="staff.referralCode" type="text"></el-input>
              </label>
            </dd>
          </dl>
        </div>
      </section>
      <section class="staff-features">
        <div class="form">
          <dl class="form-options">
            <dt>การเข้าถึง</dt>
            <dd class="staff-role">
              <label class="label">Role</label>
              <role-selector
                v-model="staff.roleId"
                :roles="roles"
              ></role-selector>
            </dd>
            <dd
              v-show="isShowFinancialInstitution"
              class="financial-institution-role"
            >
              <label class="label">สถาบันการเงิน</label>
              <financial-institution-selector
                v-model="staff.financialInstitutionId"
                :financial-institutions="financialInstitutions"
              ></financial-institution-selector>
            </dd>
          </dl>
          <div class="provider">
            <label class="label">ผู้ให้บริการ</label>
            <multiple-provider-selector
              v-if="isRuejaiPartnerAdminRole"
              v-model="staff"
              @providerIds="handleSelectedProviders"
              @isSelectedAllProviders="handleSelectedAllProviders"
            />
            <provider-selector
              v-else
              v-model="staff.provider"
              :disabled="isDisableSelectProvider"
            />
          </div>
        </div>
      </section>
      <section v-show="isShowArticleRole" class="staff-article-role">
        <div class="form">
          <dl class="form-options">
            <dt>ข่าว / บทความ</dt>
            <dd class="one-column">
              <ArticleRoleSelector
                v-model="staff.articleRole"
                :is-creating-staff="true"
                :roles="roles"
                :staff-role-id="staff.roleId"
              ></ArticleRoleSelector>
            </dd>
          </dl>
        </div>
      </section>
      <section v-show="isShowProjectSelector" class="staff-projects">
        <project-selector
          :input-projects="staff.projects"
          @setProjects="saveProjects"
        ></project-selector>
      </section>
    </div>
  </div>
</template>

<script>
import axios from "axios"
import { Input, Checkbox } from "element-ui"
import "element-ui/lib/theme-chalk/index.css"
import ProjectSelector from "@/components/helper/ProjectSelector"
import { permissionRole } from "@/lib/mapper"
import ProviderSelector from "@/lib/components/ProviderSelector"
import MultipleProviderSelector from "@/lib/components/MultipleProviderSelector"
import { isEmpty } from "ramda"
import { getCookie, Auth } from "@/lib/cookie"
import ArticleRoleSelector from "./components/ArticleRoleSelector"
import FinancialInstitutionSelector from "./components/FinancialInstitutionSelector"
import RoleSelector from "./components/RoleSelector"

export default {
  components: {
    ProjectSelector,
    RoleSelector,
    FinancialInstitutionSelector,
    ArticleRoleSelector,
    "el-input": Input,
    "el-checkbox": Checkbox,
    "provider-selector": ProviderSelector,
    "multiple-provider-selector": MultipleProviderSelector,
  },
  props: {
    roles: {
      type: Array,
      default: undefined,
    },
    financialInstitutions: {
      type: Array,
      default: undefined,
    },
  },
  data() {
    return {
      profile: {
        account: {},
        projects: [],
      },
      staff: {
        email: null,
        firstName: null,
        lastName: null,
        password: "",
        isCorpPass: false,
        referralCode: null,
        projects: [],
        articleRole: 3,
        roleId: null,
        financialInstitutionId: null,
        provider: null,
        manageProviderIds: [],
        canManageAllProviders: false,
      },
    }
  },
  computed: {
    features() {
      if (this.roles) {
        const adminRole = this.roles.filter((r) => r.role === "Admin")
        const allFeatures = adminRole[0].admin_features

        return allFeatures
      }

      return []
    },
    selectedRoleFeatures() {
      if (this.roles && this.staff.roleId) {
        const role = this.roles.find(
          (r) => r.role_id === this.staff.roleId,
        ).admin_features

        return role
      }

      return []
    },
    isShowArticleRole() {
      const articleFeatures = this.selectedRoleFeatures.map((f) => f.feature_id)
      return articleFeatures.some((e) => e <= 3)
    },
    isShowProjectSelector() {
      if (!this.roles || !this.staff || !this.staff.roleId) {
        return false
      }

      const roleName = this.roles.find(
        ({ role_id: roleId }) => roleId === this.staff.roleId,
      ).role_name

      return ![
        "Admin",
        "Call Center",
        "Home care",
        "Accounting",
        "Financial Institution",
        "Real Estate Agent",
      ].includes(roleName)
    },
    isShowFinancialInstitution() {
      if (!this.roles) {
        return false
      }

      const financialInstitutionId = this.roles.find(
        (r) => r.role === permissionRole.FI,
      ).role_id

      return [financialInstitutionId].includes(this.staff.roleId)
    },
    isDisableSelectProvider() {
      return !this.staff.roleId || !(this.staff.roleId === 18)
    },
    isRuejaiPartnerAdminRole() {
      return this.staff.roleId === 20
    },
    isSelectedManageProviderIds() {
      return !isEmpty(this.staff.manageProviderIds)
    },
    isSelectedAllProviders() {
      return this.staff.canManageAllProviders
    },
  },
  watch: {
    "staff.roleId": function staffRoleIdWatcher(roleId) {
      if (!this.roles) {
        return
      }

      const financialInstitutionId = this.roles.find(
        (r) => r.role === permissionRole.FI,
      ).role_id

      if (roleId !== financialInstitutionId) {
        this.staff.financialInstitutionId = null
      }
    },
  },
  methods: {
    handleSelectedProviders(providerIds) {
      this.staff.manageProviderIds = providerIds
    },
    handleSelectedAllProviders(isSelectedAllProviders) {
      this.staff.manageProviderIds = []
      this.staff.canManageAllProviders = isSelectedAllProviders
    },
    saveProjects(projects) {
      if (projects) {
        const projectArray = Object.values(projects)
        this.profile.projects = projectArray.map((p) =>
          parseInt(p.projectId, 10),
        )
      }
    },
    saveAccount() {
      this.profile.account = {
        email: this.staff.email,
        firstName: this.staff.firstName,
        lastName: this.staff.lastName,
        password: this.staff.password,
        isCorpPass: this.staff.isCorpPass,
        referralCode: this.staff.referralCode,
        articleRole: parseInt(this.staff.articleRole, 10),
        roleId: parseInt(this.staff.roleId, 10),
        financialInstitutionId: this.staff.financialInstitutionId,
        providerId:
          this.staff.roleId === 18 &&
          this.staff.provider &&
          this.staff.provider.id
            ? +this.staff.provider.id
            : null,
        manageProviderIds:
          this.staff.roleId === 20 && this.isSelectedManageProviderIds
            ? this.staff.manageProviderIds
            : null,
        canManageAllProviders:
          this.staff.roleId === 20 && this.isSelectedAllProviders
            ? this.staff.canManageAllProviders
            : false,
      }
    },
    async createStaffAccount() {
      if (this.staff.roleId === 7 && !this.staff.financialInstitutionId) {
        this.$message.error(
          "ไม่สามารถสร้าง staff ได้เนื่องจากไม่ได้ระบุสถาบันการเงิน",
        )
        return
      }
      if (this.staff.isCorpPass !== true) {
        if (this.staff.password.length < 8) {
          this.$message.error(
            "กรุณาใช้รหัสผ่านที่มีความยาวตั้งแต่ 8 ตัวอักษรขึ้นไป",
          )
          return
        }
      }

      if (
        this.isRuejaiPartnerAdminRole &&
        !(this.isSelectedManageProviderIds || this.isSelectedAllProviders)
      ) {
        this.$message.error("กรุณาเลือกผู้ให้บริการ หรือ กดเลือกทั้งหมด")

        return
      }

      this.saveAccount()
      this.saveProjects()

      try {
        const result = await axios.post(
          `${process.env.HOST}/admin/staffs/account`,
          {
            account: this.profile.account,
            projects: this.profile.projects,
          },
          {
            headers: {
              Authorization: `Bearer ${getCookie(Auth.AccessToken)}`,
            },
          },
        )

        this.$emit("create:staff")
        this.$router.push({
          name: "StaffEdit",
          params: {
            adminId: result.data.adminId,
          },
        })
      } catch (e) {
        this.$message.error("Could not create staff account")
        throw e
      }
    },
    handleIsCorpPassChange(value) {
      switch (value) {
        case true:
          this.staff.password = "qweasd123456"
          break
        case false:
          this.staff.password = ""
          break
        default:
          break
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.staff-info-container {
  font-size: 16px;
  background-color: $white;
  padding: 0;
  overflow: scroll;
  height: 100%;

  .staff-header {
    padding: 10px 0;

    h5 {
      font-size: 17px;
    }

    span {
      font-size: 11px;
    }
  }

  section {
    padding: 16px;
    border-top: 1px solid #e0e0e0;
  }

  dl {
    margin: 0;

    dt {
      margin-bottom: 8px;
      color: $black;
      font-size: 16px;
    }

    dd {
      label.align-right > span {
        display: inline-block;
        width: 7em;
        text-align: right;
        margin-right: 8px;
      }

      &.two-columns {
        display: table-caption;
        column-count: 2;
        margin-left: 76px;

        .select-input > span:last-child {
          width: 168px;
          display: inline-block;
        }
      }

      &.one-column {
        display: table-caption;
        column-count: 1;
        margin-left: 76px;

        .select-input > span:first-child {
          width: 30em;
          display: inline-block;
        }
      }
    }
  }

  .action-pane {
    display: flex;
    align-items: center;
  }

  .w-30 {
    display: inline-block;
    width: 30%;
  }

  .w-70 {
    display: inline-block;
    width: 69.5%;
  }

  .el-input {
    width: 210px;
  }
}

.staff-role {
  display: grid;
  grid-template-columns: 64px 1fr;
  grid-gap: 12px;

  > .label {
    margin-top: 5px;
    text-align: right;
  }
}

.financial-institution-role {
  display: grid;
  grid-template-columns: 97px 1fr;
  grid-gap: 12px;

  > .label {
    margin-top: 5px;
    text-align: right;
  }
}
</style>
